import { useLazyQuery, type FetchPolicy } from '@apollo/client';
import { METADATA_DISCOVERY } from '@content-search/graphql/metadataDiscovery';
import { transformErrors } from '@core/adapters/errors';
import type { DomainError } from '@core/domain';
import { useMemo, useCallback } from 'react';

interface MetadataFiltersResponse {
  metadataDiscovery: {
    metadataKeys: Record<string, string[]>;
    totalRecordsAnalyzed: number;
    executionTimeMs: number;
    analysisStartTime: string;
    analysisEndTime: string;
    communityId: string;
  };
}

export function useMetadataFilters() {
  const [executeGetMetadataFilters, { data, loading, error }] = useLazyQuery<MetadataFiltersResponse>(METADATA_DISCOVERY, {
    errorPolicy: 'all',
    notifyOnNetworkStatusChange: true,
  });

  const errors: DomainError[] | null = useMemo(() => {
    if (!error) return null;

    return transformErrors(error);
  }, [error]);

  const metadataFilters = useMemo(
    () =>
      data?.metadataDiscovery || {
        metadataKeys: {},
        totalRecordsAnalyzed: 0,
        executionTimeMs: 0,
        analysisStartTime: '',
        analysisEndTime: '',
        communityId: '',
      },
    [data]
  );

  const getMetadataFilters = useCallback(
    (communityId: string, fetchPolicy?: FetchPolicy) =>
      executeGetMetadataFilters({
        variables: { input: { communityId } },
        fetchPolicy: fetchPolicy || 'cache-and-network',
      }),
    [executeGetMetadataFilters]
  );

  return {
    metadataFilters,
    isFetchingMetadataFilters: loading,
    errors,
    getMetadataFilters,
  };
}
